/**
 * General top-level specific lookup of current session information goes here.
 * This file is named `current` because it contains state applicable to the current
 * notebook. This used to be referred to as `notebook` which is an overloaded term
 * and confusing. Once all state is refactored to use these selectors we should refactor
 * the state tree from `notebook` -> `current`.
 */
import { DataSourceType } from "@kaggle/sdk/datasets/databundles";
import { AcceleratorType, PersistenceMode } from "@kaggle/sdk/kernels";
import { createSelector } from "reselect";
import { JupyterSource } from "../../session/JupyterSource";
import {
  KernelLegacyStatus,
  KernelLegacyStatusName,
} from "../../telemetry/kernelTelemetry";
import { isGpuAccelerator, isTpuAccelerator } from "../../types/Accelerator";
import { EditorStatus } from "../../types/EditorStatus";
import { AppState } from "../types";

// TODO(cochamberlain): We need to rethink our redux structure. "notebook" represents
// the current interface state and is not applicable to scripts. If everything becomes
// a notebook then it makes a bit more sense but then we need a different term to
// represent "not a script". I think current or session would be better names for this.
/** Gets the current state of the session. */
export const getCurrent = (state: AppState) => state.notebook;

// TODO(cochamberlain): Pull from session. This should probably be refactored but
// cross-selector file usage is especially prone to cyclic imports leading to WSOD.
// Temporary solution is to have a private baseline import like this in files with
// selectors relying on compound state checks.
const getSessionState = (state: AppState) => state.session;

/** This is privately for complex selectors within this file. */
const getUser = createSelector([getSessionState], session => session.user);

/** This is privately for complex selectors within this file. */
const getUserId = createSelector([getUser], user =>
  user == null ? null : user.id
);

/** Selects the current editor object from Redux. */
export const getCurrentEditor = (state: AppState) => {
  return state.editor;
};

/** Selects the current kernel id. */
export const getCurrentKernelId = createSelector(
  [getCurrent],
  current => (current && current.kernel) || null
);

/** Selects the current kernel run id. */
export const getCurrentKernelRunId = createSelector(
  [getCurrent],
  current => current.run
);

/** Selects the current firebase session id. */
export const getCurrentFirebaseSessionId = createSelector(
  [getCurrent],
  current => current.firebaseSessionId
);

/** Selects whether the current notebook is private. */
export const getCurrentIsPrivate = createSelector(
  [getCurrent],
  current => current.isPrivate
);

/** Selects whether the current notebook has comments disabled. */
export const getCurrentHasCommentsDisabled = createSelector(
  [getCurrent],
  current => current.disableComments
);

/** Selects whether the current notebook allows visibility toggling. */
export const getCurrentCanToggleVisibility = createSelector(
  [getCurrent],
  current => current.canToggleVisibility
);

/** Selects the owner of the current notebook. */
export const getCurrentOwner = createSelector(
  [getCurrent],
  current => current.owner
);

/** Selects the owner id of the current notebook. */
export const getCurrentOwnerId = createSelector(
  [getCurrentOwner],
  owner => owner.userId
);

/** Selects whether the current user is the owner of this notebook. */
export const getCurrentIsOwner = createSelector(
  [getUserId, getCurrentOwnerId],
  (userId, ownerId) =>
    userId == null || ownerId == null ? false : userId === ownerId
);

/** Selects the collaborators of the current notebook. */
export const getCurrentCollaborators = createSelector(
  [getCurrent],
  current => current.collaborators ?? []
);

/** Selects the current draft sequence from redux. */
export const getCurrentSequence = createSelector([getCurrent], current =>
  current ? current.sequence : null
);

/** Selects the current ipynb from Redux. */
export const getCurrentIpynb = createSelector(
  [getCurrent],
  current => (current && current.ipynb) || null
);

/** Gets the JupyterSource wrapping current redux tracked ipynb. */
export const getCurrentJupyterSource = createSelector(
  [getCurrentIpynb],
  ipynb => (ipynb === null ? null : new JupyterSource(ipynb))
);

/** Gets the JupyterSource toNotebookContent() result for redux tracked ipynb. */
export const getCurrentNotebookContent = createSelector(
  [getCurrentJupyterSource],
  source => (source === null ? null : source.toNotebookContent())
);

/** Selects an array of serialized cells. */
export const getCurrentIpynbCells = createSelector(
  [getCurrentNotebookContent],
  content => (content === null ? [] : content.cells || [])
);

/** Selects the current title. */
export const getCurrentTitle = createSelector(
  [getCurrent],
  current => (current && current.title) || null
);

/** Selects the current persistence. */
export const getCurrentPersistence = createSelector(
  [getCurrent],
  current => (current && current.persistence) || PersistenceMode.UNSPECIFIED
);

/** Selects the current slug. */
export const getCurrentSlug = createSelector(
  [getCurrent],
  current => (current && current.slug) || null
);

/** Selects the current Docker image tag. */
export const getCurrentDockerImageTag = createSelector(
  [getCurrent],
  current => (current && current.dockerImageTag) || undefined
);

/** Selects the current worker pool name. */
export const getCurrentWorkerPoolName = createSelector(
  [getCurrent],
  current => (current && current.workerPoolName) || undefined
);

/** Selects the current data sources array. */
export const getCurrentDataSourceReferences = createSelector(
  [getCurrent],
  current => (current && current.dataSourceReferences) || []
);

/** Selects the current data sources array. */
export const getCurrentDataSources = createSelector(
  [getCurrent],
  current => (current && current.dataSources) || []
);

/** Selects the competition data source if it exists. */
export const getCompetitionDataSource = createSelector(
  [getCurrentDataSources],
  currentDataSources => {
    const competitionDataSources = currentDataSources.filter(
      s => s.sourceType === DataSourceType.COMPETITION
    );
    return competitionDataSources.length === 1
      ? competitionDataSources[0]
      : null;
  }
);

/** Selects whether Internet setting is currently enabled. */
export const getCurrentIsInternetEnabled = createSelector(
  [getCurrent],
  current => (current && current.internet) || false
);

/** Selects the unit name running this session. */
export const getBackendUnitName = createSelector(
  [getCurrent],
  current => (current && current.unitName) || "unknown"
);

/** Selects which accelerator setting is currently enabled. */
export const getCurrentAccelerator = createSelector(
  [getCurrent],
  current => (current && current.accelerator) || AcceleratorType.NONE
);

/** Selects whether the current notebook has GPU enabled. */
// TODO(herbison): Support T4s?
export const getCurrentIsGpuEnabled = createSelector(
  [getCurrentAccelerator],
  accelerator => isGpuAccelerator(accelerator)
);

/** Selects whether the current notebook has TPU enabled. */
export const getCurrentIsTpuEnabled = createSelector(
  [getCurrentAccelerator],
  accelerator => isTpuAccelerator(accelerator)
);

/** Selects the current editor status in Redux. */
export const getCurrentEditorStatus = createSelector(
  [getCurrentEditor],
  editor => editor.status
);

/** Selects the current editor status message in Redux. */
export const getCurrentEditorStatusMessage = createSelector(
  [getCurrentEditor],
  editor => editor.statusMessage
);

/** Selects whether the user can execute (fully stopped or fully running). */
export const getCanTriggerExecute = createSelector(
  [getCurrentEditorStatus],
  editorStatus =>
    editorStatus === EditorStatus.STOPPED ||
    editorStatus === EditorStatus.RUNNING
);

/** Returns the CellType of the currently selected cell. If multiple cells with different types, return 'raw'. */
export const getSelectedCellType = createSelector(
  [getCurrentEditor],
  editor => editor.selectedCellType
);

/** Returns true if the current cell will be hidden when shown in the notebook viewer. If multiple cells with different types, return null. */
export const getSelectedCellHidden = createSelector(
  [getCurrentEditor],
  editor => editor.selectedCellHidden
);

/** Returns true if the current cells output will be hidden when shown in the notebook viewer. If multiple cells with different types, return null. */
export const getSelectedCellOutputHidden = createSelector(
  [getCurrentEditor],
  editor => editor.selectedCellOutputHidden
);

/** Selects the legacy status description values used in the prior editor. */
export const getCurrentLegacyStatusName = createSelector(
  [getCurrentEditorStatus],
  (status): KernelLegacyStatusName => {
    switch (status) {
      case EditorStatus.FATAL:
        return "ERROR";
      default:
        return status;
    }
  }
);

/** Selects the legacy status enum values used in the prior editor. */
export const getCurrentLegacyStatusId = createSelector(
  [getCurrentLegacyStatusName],
  statusName => KernelLegacyStatus[statusName]
);

/** Selects the current language id. */
export const getCurrentLanguageId = createSelector(
  [getCurrent],
  current => (current && current.languageId) || null
);

/** Selects the current route path. */
export const getCurrentRoute = createSelector(
  [getCurrent],
  current => current && current.kernelIdPath
);

/** Selects the last removed datasource. */
export const getCurrentRemovedDataSource = createSelector(
  [getCurrent],
  current => current && current.dataSourceRemoved
);

/** Selects the current notebook's schedule. */
export const getCurrentSchedule = createSelector(
  [getCurrent],
  current => current.schedule
);

/** Selects the current focusOnSchedule. */
export const getCurrentFocusOnSchedule = createSelector(
  [getCurrent],
  current => current.focusOnSchedule
);
